import React, { useState } from 'react';

// Inline React port of the /blog/tools/will-lowkey-work-with-my-buzzer checker.
// Same two-question flow and outcomes, so results stay consistent across both.

const OPTION_CLASS =
  'w-full rounded-xl border border-gray-lowkey-dark px-4 py-3.5 text-left text-white transition-colors hover:border-green-lowkey hover:bg-[rgba(65,225,176,0.06)]';

const Q1_OPTIONS = [
  { value: 'yes', label: 'Yes, my phone rings' },
  { value: 'no', label: 'No, it does not' },
  { value: 'unsure', label: 'I am not sure' },
  { value: 'none', label: 'I do not have a buzzer' },
];

const Q2_OPTIONS = [
  { value: 'panel', label: 'A panel with a keypad or name directory that calls residents' },
  { value: 'video', label: 'A camera and screen intercom' },
  { value: 'handset', label: 'A wired handset mounted inside my apartment' },
  { value: 'key', label: 'Just a key or fob, nothing that calls anyone' },
];

const OUTCOMES = {
  compatible: {
    icon: '✅',
    headline: 'Lowkey will work with your buzzer.',
    body: 'Your building places a phone call when someone buzzes, which is exactly what Lowkey needs. You can forward that call to your phone and have guests, deliveries, and vendors buzzed in automatically. There is no hardware to install.',
    showCta: true,
  },
  likely: {
    icon: '🟡',
    headline: 'Lowkey most likely works, with one thing to confirm.',
    body: 'Your setup usually dials out to a phone, which is what Lowkey needs. The quickest way to be sure is to have someone buzz your unit and see if your phone rings. If it does, you are good to go. Lowkey also coexists with managed video intercoms as long as they can call a phone number.',
    showCta: true,
  },
  incompatible: {
    icon: '❌',
    headline: 'Lowkey will not work with this buzzer.',
    body: 'A wired handset inside your unit has no outbound phone call for Lowkey to forward, so it is not a fit. If your building ever moves to a telephone-entry panel that dials a phone, Lowkey would work then. We would rather tell you now than waste your time.',
    showCta: false,
  },
  none: {
    icon: 'ℹ️',
    headline: 'Lowkey is for buildings with a buzzer.',
    body: 'Lowkey works by forwarding and automating your buzzer call, so it needs a building buzzer or intercom that calls a phone. A door that only opens with a key or fob does not have a call for Lowkey to handle.',
    showCta: false,
  },
};

const resultFor = (answers) => {
  if (answers.q1 === 'yes') return 'compatible';
  if (answers.q1 === 'none') return 'none';
  if (answers.q2 === 'panel') return answers.q1 === 'no' ? 'likely' : 'compatible';
  if (answers.q2 === 'video') return 'likely';
  if (answers.q2 === 'handset') return 'incompatible';
  if (answers.q2 === 'key') return 'none';
  return 'likely';
};

const BuzzerChecker = ({ ctaLabel = 'Start your 14 day trial' }) => {
  const [step, setStep] = useState('q1');
  const [answers, setAnswers] = useState({ q1: null, q2: null });

  const answerQ1 = (value) => {
    setAnswers({ q1: value, q2: null });
    setStep(value === 'yes' || value === 'none' ? 'result' : 'q2');
  };

  const answerQ2 = (value) => {
    setAnswers((prev) => ({ ...prev, q2: value }));
    setStep('result');
  };

  const restart = () => {
    setAnswers({ q1: null, q2: null });
    setStep('q1');
  };

  return (
    <div className="rounded-2xl border border-gray-lowkey-dark p-6 text-left sm:p-8">
      {step === 'q1' && (
        <div>
          <p className="font-space-grotesk-semi-bold text-xl text-white">
            When a visitor buzzes your unit, does your phone ring?
          </p>
          <div className="mt-5 grid gap-3">
            {Q1_OPTIONS.map((opt) => (
              <button key={opt.value} type="button" className={OPTION_CLASS} onClick={() => answerQ1(opt.value)}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 'q2' && (
        <div>
          <p className="font-space-grotesk-semi-bold text-xl text-white">What is at your building's front door?</p>
          <div className="mt-5 grid gap-3">
            {Q2_OPTIONS.map((opt) => (
              <button key={opt.value} type="button" className={OPTION_CLASS} onClick={() => answerQ2(opt.value)}>
                {opt.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="mt-5 text-sm text-gray-lowkey-medium underline"
            onClick={() => setStep('q1')}
          >
            Back
          </button>
        </div>
      )}

      {step === 'result' &&
        (() => {
          const outcome = OUTCOMES[resultFor(answers)];
          return (
            <div>
              <p className="text-3xl">{outcome.icon}</p>
              <p className="mt-2 font-space-grotesk-semi-bold text-xl text-white">{outcome.headline}</p>
              <p className="mt-3 text-gray-lowkey-medium">{outcome.body}</p>
              {outcome.showCta && (
                <div className="mt-6">
                  <a
                    href="#download"
                    className="inline-flex items-center justify-center rounded-lg border border-green-lowkey bg-green-lowkey py-3 px-5 text-center text-base font-medium text-black hover:bg-black hover:text-green-lowkey focus:ring-4 focus:ring-gray-800"
                  >
                    {ctaLabel}
                  </a>
                </div>
              )}
              <button type="button" className="mt-6 block text-sm text-gray-lowkey-medium underline" onClick={restart}>
                Start over
              </button>
            </div>
          );
        })()}
    </div>
  );
};

export default BuzzerChecker;
